import { ContentType } from '@quromedical/fhir-common';
import type {
  GetAllMediaResponse,
  MediaGetResponse,
  PartialTaskWithId,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { DisplayView } from 'components/fhir';
import type { ColumnType } from 'components/table';
import { TaskColumn } from 'core/task';
import { type FileType, MediaPreview, Text } from 'design-system';
import { MediaApi } from 'integration/aggregate';
import { strings } from 'strings';

import { MediaUpdate } from './mediaUpdate';

export interface TableMedia {
  id: string;
  url: string;
  operator: fhir4.Reference;
  title: string;
  contentType: ContentType;
  update: MediaGetResponse;
  task?: PartialTaskWithId;
}

const mediaTypeMap: Partial<Record<ContentType, FileType>> = {
  [ContentType.pdf]: 'pdf',
  [ContentType.jpeg]: 'image',
  [ContentType.png]: 'image',
};

const api = new MediaApi();

const getMediaUrl =
  (patientId: string, mediaId: string) => async (): Promise<string> => {
    const media = await api.createDownloadUrl(patientId, mediaId);

    return media.url;
  };

interface MediaColumnsProps {
  patientId: string;
  hasTaskPermissions: boolean;
  hasMediaPermissions: boolean;
}

export const mediaColumns = ({
  patientId,
  hasTaskPermissions,
  hasMediaPermissions,
}: MediaColumnsProps): ColumnType<TableMedia>[] => {
  const baseCols: ColumnType<TableMedia>[] = [
    {
      header: strings.MediaFileNameSubheading,
      Cell: ({ value }) => <Text>{value}</Text>,
      accessor: 'title',
    } as ColumnType<TableMedia, 'title'>,
    {
      header: strings.MediaPractitionerSubheading,
      Cell: ({ value }) => <DisplayView reference={value} />,
      accessor: 'operator',
    } as ColumnType<fhir4.Media, 'operator'>,
    {
      accessor: 'id',
      header: strings.MediaDownloadSubheading,
      Cell: ({ row, value }) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const fileType = mediaTypeMap[row.original.contentType];

        return (
          <MediaPreview
            id={value}
            type={fileType}
            getUrl={getMediaUrl(patientId, value)}
            title={row.original.title}
          />
        );
      },
    } as ColumnType<TableMedia, 'id'>,
  ];

  const mediaUpdate: ColumnType<TableMedia, 'update'> = {
    accessor: 'update',
    header: strings.LabelEditTitle,
    Cell: ({ row }) => (
      <MediaUpdate
        patientId={patientId}
        media={row.original.update}
        title={row.original.title}
      />
    ),
  };

  const task: ColumnType<TableMedia, 'task'> = {
    accessor: 'task',
    header: '',
    Cell: ({ row }) => (
      <TaskColumn
        patientId={patientId}
        focusId={row.original.id}
        focusType="Media"
      />
    ),
  };

  return compact([
    ...baseCols,
    hasMediaPermissions && mediaUpdate,
    hasTaskPermissions && task,
  ]);
};

export const getDataFromResponse = (
  res: GetAllMediaResponse,
): Partial<TableMedia>[] =>
  res.media.map((p) => ({
    id: p.id,
    operator: p.createdBy,
    title: p.title,
    url: p.id,
    contentType: p.contentType,
    update: p,
  }));
